/**
 * Agent Signatures API
 *
 * API endpoints for managing agent signatures used in retainer agreements
 * and other legal documents.
 */

import { AxiosResponse } from 'axios';
import client from 'api/client';
import type {
    AgentSignature,
    SignatureCreateRequest,
    SignatureUpdateRequest,
} from 'types/signature';

/**
 * API response wrapper for signature operations
 */
export interface SignatureResponse {
    signature?: AgentSignature;
    signatures?: AgentSignature[];
    message?: string;
    error?: string;
}

/**
 * Create a new agent signature
 *
 * @param data - Signature creation payload
 * @returns Promise resolving to the created signature
 *
 * @example
 * ```typescript
 * const signature = await signaturesApi.create({
 *   creation_method: 'typed',
 *   typed_text: 'John Smith',
 *   font_family: 'Allura',
 *   color: '#000000',
 *   size: 'medium',
 *   is_default: true
 * });
 * ```
 */
export function create(data: SignatureCreateRequest): Promise<AxiosResponse<AgentSignature>> {
    return client.post<AgentSignature>('/service-provider/signatures', data);
}

/**
 * Get all signatures for the current user
 *
 * @returns Promise resolving to array of signatures
 *
 * @example
 * ```typescript
 * const signatures = await signaturesApi.list();
 * ```
 */
export function list(): Promise<AxiosResponse<AgentSignature[]>> {
    return client.get<AgentSignature[]>('/service-provider/signatures');
}

/**
 * Get a specific signature by ID
 *
 * @param id - Signature ID
 * @returns Promise resolving to the signature
 *
 * @example
 * ```typescript
 * const signature = await signaturesApi.get(123);
 * ```
 */
export function get(id: number): Promise<AxiosResponse<AgentSignature>> {
    return client.get<AgentSignature>(`/service-provider/signatures/${id}`);
}

/**
 * Update signature metadata
 * Note: Only metadata fields can be updated (display_name, title, signature_name)
 *
 * @param id - Signature ID
 * @param data - Fields to update
 * @returns Promise resolving to the updated signature
 *
 * @example
 * ```typescript
 * const signature = await signaturesApi.update(123, {
 *   display_name: 'Dr. John Smith',
 *   title: 'RCIC, Immigration Consultant'
 * });
 * ```
 */
export function update(
    id: number,
    data: SignatureUpdateRequest
): Promise<AxiosResponse<AgentSignature>> {
    return client.put<AgentSignature>(`/service-provider/signatures/${id}`, data);
}

/**
 * Delete a signature (soft delete - marks as inactive)
 *
 * @param id - Signature ID
 * @returns Promise resolving when deletion is complete
 *
 * @example
 * ```typescript
 * await signaturesApi.remove(123);
 * ```
 */
export function remove(id: number): Promise<AxiosResponse<void>> {
    return client.delete<void>(`/service-provider/signatures/${id}`);
}

/**
 * Set a signature as the default
 * This will unmark any previous default signature
 *
 * @param id - Signature ID to set as default
 * @returns Promise resolving to the updated signature
 *
 * @example
 * ```typescript
 * const signature = await signaturesApi.setDefault(123);
 * ```
 */
export function setDefault(id: number): Promise<AxiosResponse<AgentSignature>> {
    return client.put<AgentSignature>(`/service-provider/signatures/${id}/default`);
}

/**
 * Get the default signature for a specific agent/user
 *
 * @param userId - User ID of the agent
 * @returns Promise resolving to the agent's default signature
 *
 * @example
 * ```typescript
 * const signature = await signaturesApi.getAgentDefaultSignature(456);
 * ```
 */
export function getAgentDefaultSignature(
    userId: number
): Promise<AxiosResponse<AgentSignature | null>> {
    return client.get<AgentSignature | null>(
        `/service-provider/agents/${userId}/default-signature`
    );
}

/**
 * Signatures API object with all methods
 * Can be imported and used with object notation
 *
 * @example
 * ```typescript
 * import { signaturesApi } from 'api';
 *
 * const signatures = await signaturesApi.list();
 * ```
 */
export const signaturesApi = {
    create,
    list,
    get,
    update,
    remove,
    setDefault,
    getAgentDefaultSignature,
};

export default signaturesApi;
